import { useState, useEffect } from 'react';
import {
    MoreVertical,
    CreditCard,
    XCircle,
    Building2,
    ExternalLink,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isAfter,
    isSameMonth,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
} from 'date-fns';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from "@/components/ui/button";

import { apiFetch } from '@/utils/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNavigate } from '@tanstack/react-router';
import { PaymentInstrumentModal } from './PaymentInstrumentModal';
import { useI18n } from '../hooks/useI18n';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
    company_id?: string;
}

interface Invoice {
    id: string;
    payment_status?: string;
    cost_center_id?: string;
    vendor?: {
        id: string;
        name: string;
    };
    cost_center?: CostCenter;
    total_amount?: number;
    created_at: string;
    invoice_date?: string;
    due_date?: string;
    status: string;
    invoice_number?: string;
    file_path?: string;
    original_filename?: string;
    google_drive_file_id?: string;
    has_invoice_photo?: boolean;
    company_id?: string;
}

interface InvoiceKebabMenuProps {
    invoice: Invoice;
    costCenters?: CostCenter[];
    onInvoiceUpdate?: (invoice: Invoice) => void;
}

function localDateISO(d: Date = new Date()): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function parseLocalDate(iso: string): Date {
    const parts = iso.split('-').map((x) => parseInt(x, 10));
    const [y, m, d] = parts;
    if (!y || !m || !d) return new Date();
    return new Date(y, m - 1, d);
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function MarkPaidMonthCalendar({
    selectedIso,
    onSelectIso,
}: {
    selectedIso: string;
    onSelectIso: (iso: string) => void;
}) {
    const today = startOfDay(new Date());
    const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(parseLocalDate(selectedIso)));

    useEffect(() => {
        setVisibleMonth(startOfMonth(parseLocalDate(selectedIso)));
    }, [selectedIso]);

    const monthStart = startOfMonth(visibleMonth);
    const monthEnd = endOfMonth(visibleMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    const nextMonthStart = startOfMonth(addMonths(visibleMonth, 1));
    const disableNextMonth = isAfter(nextMonthStart, startOfMonth(today));

    return (
        <div className="rounded-md border bg-background p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => setVisibleMonth(subMonths(visibleMonth, 1))}
                    aria-label="Previous month"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium capitalize">{format(visibleMonth, 'MMMM yyyy')}</span>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    disabled={disableNextMonth}
                    onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
                    aria-label="Next month"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
            <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">
                {WEEKDAY_LABELS.map((label) => (
                    <div key={label}>{label}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                    const iso = localDateISO(day);
                    const inMonth = isSameMonth(day, visibleMonth);
                    const selected = selectedIso === iso;
                    const dayStart = startOfDay(day);
                    const disabled = isAfter(dayStart, today);

                    return (
                        <button
                            key={iso}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSelectIso(iso)}
                            className={[
                                'flex h-9 w-full items-center justify-center rounded-md text-sm transition-colors',
                                !inMonth && 'text-muted-foreground/40',
                                disabled && 'cursor-not-allowed opacity-40',
                                selected && 'bg-primary text-primary-foreground font-medium',
                                !selected && !disabled && inMonth && 'hover:bg-muted',
                                !selected && !disabled && !inMonth && 'hover:bg-muted/50',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            aria-label={format(day, 'PPP')}
                            aria-pressed={selected}
                        >
                            {format(day, 'd')}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function InvoiceKebabMenu({ invoice, costCenters = [], onInvoiceUpdate }: InvoiceKebabMenuProps) {
    const [loading, setLoading] = useState(false);
    const { isAdmin } = useCurrentUser();
    const navigate = useNavigate();
    const { t } = useI18n();

    // Payment Instrument Modal State
    const [showInstrumentModal, setShowInstrumentModal] = useState(false);
    const [availableInstruments, setAvailableInstruments] = useState<any[]>([]);
    const [pendingPaidOn, setPendingPaidOn] = useState<string>(localDateISO());
    const [showPaidDateDialog, setShowPaidDateDialog] = useState(false);
    const [pickedPaidDate, setPickedPaidDate] = useState<string>(localDateISO());

    const fetchPaymentInstruments = async () => {
        try {
            // Use company_id directly from invoice (populated from nav_credentials)
            const companyId = invoice.company_id;

            // Build query params - only add company_id if it exists
            const queryParams = companyId ? `?company_id=${companyId}` : '';

            const response = await apiFetch(`/api/payment-instruments/${queryParams}`);
            if (response.ok) {
                const data = await response.json();
                // Only active instruments are returned from backend
                return data.payment_instruments || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching payment instruments:', error);
            return [];
        }
    };

    const prepareMarkPaid = async (paidOn: string) => {
        setLoading(true);
        try {
            const instruments = await fetchPaymentInstruments();

            if (instruments.length === 0) {
                if (confirm(t('payments.noActiveInstruments', 'common'))) {
                    await performMarkPaid(null, paidOn);
                }
            } else if (instruments.length === 1) {
                await performMarkPaid(instruments[0].id, paidOn);
            } else {
                setPendingPaidOn(paidOn);
                setAvailableInstruments(instruments);
                setShowInstrumentModal(true);
            }
        } catch (error) {
            console.error('Error preparing mark paid:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkPaidTodayClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading) return;
        await prepareMarkPaid(localDateISO());
    };

    const handleMarkPaidPickDateOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading) return;
        setPickedPaidDate(localDateISO());
        setShowPaidDateDialog(true);
    };

    const handlePaidDateDialogContinue = () => {
        if (!pickedPaidDate) return;
        setShowPaidDateDialog(false);
        void prepareMarkPaid(pickedPaidDate);
    };

    const performMarkPaid = async (paymentInstrumentId: string | null, paidOn: string) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            params.set('paid_on', paidOn);
            if (paymentInstrumentId) {
                params.set('payment_instrument_id', paymentInstrumentId);
            }
            const queryString = params.toString();
            const response = await apiFetch(
                `/api/invoices/${invoice.id}/mark-paid?${queryString}`,
                { method: 'POST' }
            );

            if (response.ok) {
                const updatedInvoice = await response.json();
                onInvoiceUpdate?.(updatedInvoice);
                setShowInstrumentModal(false);
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'Failed to mark invoice as paid'}`);
            }
        } catch (error) {
            console.error('Error marking invoice as paid:', error);
            alert('Failed to mark invoice as paid');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkUnpaid = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading) return;

        try {
            setLoading(true);
            const response = await apiFetch(`/api/invoices/${invoice.id}/mark-unpaid`, {
                method: 'POST'
            });

            if (response.ok) {
                const updatedInvoice = await response.json();
                onInvoiceUpdate?.(updatedInvoice);
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'Failed to mark invoice as unpaid'}`);
            }
        } catch (error) {
            console.error('Error marking invoice as unpaid:', error);
            alert('Failed to mark invoice as unpaid');
        } finally {
            setLoading(false);
        }
    };

    const handleChangeCostCenter = async (costCenterId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (loading) return;

        try {
            setLoading(true);
            const response = await apiFetch(`/api/invoices/${invoice.id}/cost-center?cost_center_id=${costCenterId}`, {
                method: 'PATCH'
            });

            if (response.ok) {
                const updatedInvoice = await response.json();
                onInvoiceUpdate?.(updatedInvoice);
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'Failed to change cost center'}`);
            }
        } catch (error) {
            console.error('Error changing cost center:', error);
            alert('Failed to change cost center');
        } finally {
            setLoading(false);
        }
    };

    const handleFindPaymentTransaction = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Navigate to payments page with invoice number filter
        navigate({
            to: '/payments',
            search: {
                search: invoice.invoice_number || ''
            }
        });
    };

    const isPaid = invoice.payment_status === 'paid' || invoice.payment_status === 'overpaid';
    const isUnpaid = invoice.payment_status === 'unpaid';

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        size="sm"
                        variant='outline'
                        disabled={loading}
                        onClick={(e) => e.stopPropagation()} // Prevent row click
                    >
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">{t('actions.openMenu', 'common')}</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[14rem]">
                    {/* Payment Status Actions */}
                    {!isPaid && (
                        <>
                            <DropdownMenuItem onClick={handleMarkPaidTodayClick} disabled={loading}>
                                <CreditCard className="mr-2 h-4 w-4" />
                                {t('invoices.markAsPaidToday', 'finance')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleMarkPaidPickDateOpen} disabled={loading}>
                                <CalendarDays className="mr-2 h-4 w-4" />
                                {t('invoices.markAsPaidPickDate', 'finance')}
                            </DropdownMenuItem>
                        </>
                    )}

                    {!isUnpaid && (
                        <DropdownMenuItem onClick={handleMarkUnpaid} disabled={loading}>
                            <XCircle className="mr-2 h-4 w-4" />
                            {t('invoices.markAsUnpaid', 'finance')}
                        </DropdownMenuItem>
                    )}

                    {isPaid && (
                        <DropdownMenuItem onClick={handleFindPaymentTransaction} disabled={loading}>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            {t('invoices.findPaymentTransaction', 'finance')}
                        </DropdownMenuItem>
                    )}

                    {/* Admin-only actions */}
                    {isAdmin && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger disabled={loading}>
                                    <Building2 className="mr-2 h-4 w-4" />
                                    {t('invoices.changeCostCenter', 'finance')}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-48">
                                    {costCenters.map((costCenter) => (
                                        <DropdownMenuItem
                                            key={costCenter.id}
                                            onClick={(e) => handleChangeCostCenter(costCenter.id, e)}
                                            disabled={loading || costCenter.id === invoice.cost_center_id}
                                        >
                                            {costCenter.name}
                                            {costCenter.id === invoice.cost_center_id && (
                                                <span className="ml-auto text-xs text-muted-foreground">{t('common.current', 'common')}</span>
                                            )}
                                        </DropdownMenuItem>
                                    ))}
                                    {costCenters.length === 0 && (
                                        <DropdownMenuItem disabled>
                                            {t('costCenters.noCostCenters', 'common')}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <PaymentInstrumentModal
                open={showInstrumentModal}
                onOpenChange={setShowInstrumentModal}
                instruments={availableInstruments}
                onSelect={(id) => void performMarkPaid(id, pendingPaidOn)}
                loading={loading}
            />

            <Dialog open={showPaidDateDialog} onOpenChange={setShowPaidDateDialog}>
                <DialogContent
                    className="sm:max-w-md"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDownOutside={(e) => e.stopPropagation()}
                >
                    <DialogHeader>
                        <DialogTitle>
                            {t('invoices.markAsPaidPickDateTitle', 'finance')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('invoices.markAsPaidPickDateDesc', 'finance')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-1">
                        <MarkPaidMonthCalendar
                            selectedIso={pickedPaidDate}
                            onSelectIso={setPickedPaidDate}
                        />
                    </div>
                    <DialogFooter className="gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPaidDateDialog(false)}
                        >
                            {t('actions.cancel', 'common')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handlePaidDateDialogContinue}
                            disabled={!pickedPaidDate || loading}
                        >
                            {t('actions.confirm', 'common')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
